import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "./Auth/AuthContext";
import { fetchOrders } from "../utils/shopifyApi";

const PAGE_SIZE = 20;

const styles = {
    container: {
        maxWidth: "1400px",
        margin: "0 auto",
        padding: "24px",
        boxSizing: "border-box",
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    },
    breadcrumb: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '14px',
        color: '#666',
        marginBottom: '24px'
    },
    breadcrumbLink: {
        color: '#666',
        textDecoration: 'none',
        cursor: 'pointer',
        transition: 'color 0.2s'
    },
    breadcrumbSeparator: {
        color: '#999'
    },
    title: {
        fontSize: "24px",
        fontWeight: 700,
        color: "#1a1a1a",
        margin: "0 0 20px 0",
    },
    tableWrap: {
        backgroundColor: "#fff",
        borderRadius: "8px",
        boxShadow: "0 2px 6px rgba(15, 23, 42, 0.04)",
        overflow: "hidden",
        border: "1px solid rgba(0,0,0,0.06)",
    },
    table: {
        width: "100%",
        borderCollapse: "collapse",
        fontSize: "14px",
    },
    th: {
        textAlign: "left",
        padding: "12px 16px",
        fontWeight: 600,
        color: "#374151",
        backgroundColor: "#f9fafb",
        borderBottom: "1px solid #e5e7eb",
    },
    td: {
        padding: "12px 16px",
        borderBottom: "1px solid #f3f4f6",
        color: "#374151",
    },
    trHover: {
        backgroundColor: "#fafafa",
    },
    badge: {
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: "4px",
        fontSize: "12px",
        fontWeight: 500,
    },
    pagination: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginTop: "16px",
        fontSize: "14px",
        color: "#6b7280",
    },
    pageButtons: {
        display: "flex",
        gap: "8px",
        alignItems: "center",
    },
    pageButton: {
        padding: "8px 14px",
        backgroundColor: "#fff",
        border: "1px solid #d1d5db",
        borderRadius: "6px",
        fontSize: "14px",
        fontWeight: 500,
        cursor: "pointer",
        color: "#374151",
    },
    pageButtonDisabled: {
        padding: "8px 14px",
        backgroundColor: "#f9fafb",
        border: "1px solid #e5e7eb",
        borderRadius: "6px",
        fontSize: "14px",
        color: "#9ca3af",
        cursor: "default",
    },
    stateContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "280px",
        fontSize: "15px",
        color: "#6b7280",
    },
    errorContainer: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "280px",
        gap: "12px",
    },
    errorText: { fontSize: "15px", color: "#ef4444" },
    retryButton: {
        padding: "8px 20px",
        backgroundColor: "#111827",
        color: "#fff",
        border: "none",
        borderRadius: "6px",
        fontSize: "14px",
        fontWeight: 500,
        cursor: "pointer",
    },
};

function formatDate(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleDateString(undefined, { dateStyle: "short", timeStyle: "short" });
    } catch {
        return iso;
    }
}

function statusBadge(status, type = "financial") {
    if (!status) return <span style={{ color: "#9ca3af" }}>—</span>;
    const colors = {
        paid: "#d1fae5",
        pending: "#fef3c7",
        refunded: "#fee2e2",
        fulfilled: "#d1fae5",
        unfulfilled: "#f3f4f6",
        partial: "#e0e7ff",
    };
    const color = colors[String(status).toLowerCase()] || "#f3f4f6";
    return (
        <span style={{ ...styles.badge, backgroundColor: color, color: "#374151" }}>
            {status}
        </span>
    );
}

export default function OrdersList() {
    const { tokens } = useAuth();
    const [orders, setOrders] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    const load = useCallback(
        async (pageNum) => {
            setLoading(true);
            setError(null);
            const result = await fetchOrders({
                page: pageNum,
                limit: PAGE_SIZE,
                accessToken: tokens?.accessToken,
            });
            if (result.ok && result.data) {
                setOrders(result.data.orders ?? []);
                setTotal(result.data.total ?? 0);
            } else {
                setError(result.error || "Failed to load orders");
            }
            setLoading(false);
        },
        [tokens?.accessToken]
    );

    useEffect(() => {
        load(page);
    }, [page, load]);

    if (loading && orders.length === 0) {
        return (
            <div style={styles.container}>
                {/* Breadcrumb */}
                <div style={styles.breadcrumb}>
                    <span style={{color: '#1a1a1a', fontWeight: 500}}>Orders</span>
                </div>
                
                <h1 style={styles.title}>Orders</h1>
                <div style={styles.stateContainer}>Loading orders…</div>
            </div>
        );
    }

    if (error && orders.length === 0) {
        return (
            <div style={styles.container}>
                {/* Breadcrumb */}
                <div style={styles.breadcrumb}>
                    <span style={{color: '#1a1a1a', fontWeight: 500}}>Orders</span>
                </div>
                
                <h1 style={styles.title}>Orders</h1>
                <div style={styles.errorContainer}>
                    <span style={styles.errorText}>{error}</span>
                    <button style={styles.retryButton} onClick={() => load(page)}>
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            {/* Breadcrumb */}
            <div style={styles.breadcrumb}>
                <span style={{color: '#1a1a1a', fontWeight: 500}}>Orders</span>
            </div>
            
            <h1 style={styles.title}>Orders</h1>
            <div style={styles.tableWrap}>
                <table style={styles.table}>
                    <thead>
                        <tr>
                            <th style={styles.th}>Order #</th>
                            <th style={styles.th}>Name</th>
                            <th style={styles.th}>Email</th>
                            <th style={styles.th}>Total</th>
                            <th style={styles.th}>Financial</th>
                            <th style={styles.th}>Fulfillment</th>
                            <th style={styles.th}>Source</th>
                            <th style={styles.th}>Created</th>
                        </tr>
                    </thead>
                    <tbody>
                        {orders.length === 0 ? (
                            <tr>
                                <td colSpan={8} style={{ ...styles.td, textAlign: "center", color: "#9ca3af" }}>
                                    No orders found
                                </td>
                            </tr>
                        ) : (
                            orders.map((o) => (
                                <tr key={o.id} style={{ backgroundColor: "#fff" }}>
                                    <td style={styles.td}>{o.orderNumber ?? o.refId ?? "—"}</td>
                                    <td style={styles.td}>{o.orderName ?? "—"}</td>
                                    <td style={styles.td}>{o.email ?? "—"}</td>
                                    <td style={styles.td}>
                                        {o.totalPrice != null ? `${o.currency || "USD"} ${o.totalPrice}` : "—"}
                                    </td>
                                    <td style={styles.td}>{statusBadge(o.financialStatus)}</td>
                                    <td style={styles.td}>{statusBadge(o.fulfillmentStatus, "fulfillment")}</td>
                                    <td style={styles.td}>{o.sourceRef ?? "—"}</td>
                                    <td style={styles.td}>{formatDate(o.createdAt)}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
            {total > 0 && (
                <div style={styles.pagination}>
                    <span>
                        Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
                    </span>
                    <div style={styles.pageButtons}>
                        <button
                            style={page <= 1 ? styles.pageButtonDisabled : styles.pageButton}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            disabled={page <= 1}
                        >
                            Previous
                        </button>
                        <span>
                            Page {page} of {totalPages}
                        </span>
                        <button
                            style={page >= totalPages ? styles.pageButtonDisabled : styles.pageButton}
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                            disabled={page >= totalPages}
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
